import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { createPromoCode, promoErrorMessage, type PromoScope } from '@/lib/promoApi';
import { dateTimeLocalToUnix } from '@/lib/promoDates';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  walletAddress: string;
  appid: string;
  isAdmin: boolean;
  onCreated: () => void;
};

type FormState = {
  scope: PromoScope;
  code: string;
  percentOff: string;
  maxRedemptions: string;
  expiresAt: string; // datetime-local string
};

const INITIAL_FORM: FormState = {
  scope: 'tenant',
  code: '',
  percentOff: '',
  maxRedemptions: '',
  expiresAt: '',
};

export const CreatePromoCodeDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  walletAddress,
  appid,
  isAdmin,
  onCreated,
}) => {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm(INITIAL_FORM);
  }, [open]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const handleSubmit = async () => {
    const percent = Math.trunc(Number(form.percentOff));
    if (!Number.isFinite(percent) || percent < 1 || percent > 100) {
      toast.error('Discount must be a whole number between 1 and 100%.');
      return;
    }

    let expiresAt: number | null = null;
    if (form.expiresAt) {
      const unix = dateTimeLocalToUnix(form.expiresAt);
      if (unix == null) {
        toast.error('Enter a valid expiry date.');
        return;
      }
      expiresAt = unix;
    }

    let maxRedemptions: number | null = null;
    if (form.maxRedemptions.trim()) {
      const max = Math.trunc(Number(form.maxRedemptions));
      if (!Number.isFinite(max) || max < 1) {
        toast.error('Max redemptions must be a positive whole number.');
        return;
      }
      maxRedemptions = max;
    }

    setSubmitting(true);
    try {
      await createPromoCode(walletAddress, {
        scope: form.scope,
        ...(form.scope === 'tenant' ? { appid } : {}),
        code: form.code.trim() || undefined,
        percentOff: percent,
        expiresAt,
        maxRedemptions,
      });
      toast.success('Promo code created');
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(promoErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid='create-promo-code-dialog'
        className='max-w-lg bg-[var(--pp-surface)] text-[var(--pp-text)]'
      >
        <DialogHeader>
          <DialogTitle>New promo code</DialogTitle>
          <DialogDescription>
            Percent-off discount applied at checkout. A 100% code settles a $0 session and still fires the
            signed webhook, so the tenant grants access through the same path.
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-4'>
          {isAdmin && (
            <div className='grid gap-1.5'>
              <Label>Scope</Label>
              <Select value={form.scope} onValueChange={(value) => update('scope', value as PromoScope)}>
                <SelectTrigger className='bg-[var(--pp-subtle)]' data-testid='promo-scope-select'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='tenant'>This tenant only</SelectItem>
                  <SelectItem value='global'>Global (all tenants)</SelectItem>
                </SelectContent>
              </Select>
              <p className='text-xs text-[var(--pp-text-subtle)]'>
                {form.scope === 'global'
                  ? 'Valid for any tenant. Reduces what is swept on every tenant — admin only.'
                  : `Valid only for ${appid}.`}
              </p>
            </div>
          )}

          <div className='grid gap-1.5'>
            <Label htmlFor='promo-code-input'>Code</Label>
            <Input
              id='promo-code-input'
              data-testid='promo-code-input'
              value={form.code}
              onChange={(e) => update('code', e.target.value)}
              placeholder='Leave blank to auto-generate'
              className='bg-[var(--pp-subtle)] font-mono uppercase'
            />
          </div>

          <div className='grid gap-3 sm:grid-cols-2'>
            <div className='grid gap-1.5'>
              <Label htmlFor='promo-percent-input'>Discount %</Label>
              <Input
                id='promo-percent-input'
                data-testid='promo-percent-input'
                type='number'
                min={1}
                max={100}
                value={form.percentOff}
                onChange={(e) => update('percentOff', e.target.value)}
                placeholder='1–100'
                className='bg-[var(--pp-subtle)]'
              />
            </div>
            <div className='grid gap-1.5'>
              <Label htmlFor='promo-max-input'>Max redemptions</Label>
              <Input
                id='promo-max-input'
                data-testid='promo-max-input'
                type='number'
                min={1}
                value={form.maxRedemptions}
                onChange={(e) => update('maxRedemptions', e.target.value)}
                placeholder='Unlimited'
                className='bg-[var(--pp-subtle)]'
              />
            </div>
          </div>

          <div className='grid gap-1.5'>
            <Label htmlFor='promo-expiry-input'>Expires</Label>
            <Input
              id='promo-expiry-input'
              data-testid='promo-expiry-input'
              type='datetime-local'
              value={form.expiresAt}
              onChange={(e) => update('expiresAt', e.target.value)}
              className='bg-[var(--pp-subtle)]'
            />
            <p className='text-xs text-[var(--pp-text-subtle)]'>Optional — leave blank for no expiry.</p>
          </div>
        </div>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type='button'
            className='gap-2'
            data-testid='promo-create-submit'
            onClick={handleSubmit}
            disabled={submitting}
            style={{ background: 'var(--pp-action)', color: '#fff' }}
          >
            <Ticket size={14} />
            {submitting ? 'Creating…' : 'Create code'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePromoCodeDialog;
